import type { AppliedFilter, FilterOption } from "@/components/BlotterFilterBar";
import type { OrgListParams } from "@/interfaces/organization";
import type { AccountLead, UserListParams } from "@/interfaces/user";

/** Field ids rendered by the Organizations blotter's filter panel (D3, prototype order). */
export type OrgFilterFieldId = "organization" | "plan" | "lifecycle" | "accountLead";
/** Field ids rendered by the Users blotter's filter panel (D2); "gfg" stays a one-click boolean. */
export type UserFilterFieldId = "role" | "status" | "plan" | "gfg" | "demo";

/** Wire token for "no account lead" (D5/D6), both as a panel option value and a query token. */
export const ACCOUNT_LEAD_UNASSIGNED = "unassigned";

function findFilter(filters: AppliedFilter[], fieldId: string): AppliedFilter | undefined {
  return filters.find((f) => f.fieldId === fieldId);
}

function isNotOp(filter: AppliedFilter | undefined): boolean {
  return filter?.op === "isnot";
}

export function orgListParams(q: { search: string; filters: AppliedFilter[] }): OrgListParams {
  const params: OrgListParams = {};
  const search = q.search.trim();
  if (search) params.search = search;

  const organization = findFilter(q.filters, "organization");
  if (organization?.values.length) {
    params.organizationIds = organization.values;
    if (isNotOp(organization)) params.organizationsOp = "isnot";
  }
  const plan = findFilter(q.filters, "plan");
  if (plan?.values.length) {
    params.plans = plan.values;
    if (isNotOp(plan)) params.plansOp = "isnot";
  }
  const lifecycle = findFilter(q.filters, "lifecycle");
  if (lifecycle?.values.length) {
    params.lifecycles = lifecycle.values;
    if (isNotOp(lifecycle)) params.lifecyclesOp = "isnot";
  }
  const accountLead = findFilter(q.filters, "accountLead");
  if (accountLead?.values.length) {
    params.accountLeads = accountLead.values;
    if (isNotOp(accountLead)) params.accountLeadsOp = "isnot";
  }
  return params;
}

export function userListParams(q: { search: string; filters: AppliedFilter[] }): UserListParams {
  const params: UserListParams = {};
  const search = q.search.trim();
  if (search) params.search = search;

  const role = findFilter(q.filters, "role");
  if (role?.values.length) {
    params.roles = role.values;
    if (isNotOp(role)) params.rolesOp = "isnot";
  }
  const status = findFilter(q.filters, "status");
  if (status?.values.length) {
    params.statuses = status.values;
    if (isNotOp(status)) params.statusesOp = "isnot";
  }
  const plan = findFilter(q.filters, "plan");
  if (plan?.values.length) {
    params.plans = plan.values;
    if (isNotOp(plan)) params.plansOp = "isnot";
  }
  const gfg = findFilter(q.filters, "gfg");
  if (gfg?.values.length) params.gfgOnly = true;
  const demo = findFilter(q.filters, "demo");
  if (demo?.values.length) params.demoOnly = true;

  return params;
}

/** Comma-joins `values` under `key` in `query`, plus `opKey: "isnot"` when `op` is isnot. */
function putList(
  query: Record<string, string>,
  key: string,
  values: string[] | undefined,
  opKey: string,
  op: "isnot" | undefined,
): void {
  if (!values?.length) return;
  query[key] = values.join(",");
  if (op === "isnot") query[opKey] = "isnot";
}

export function orgListQuery(p: OrgListParams): Record<string, string> {
  const query: Record<string, string> = {};
  if (p.search) query.search = p.search;
  putList(query, "organizationIds", p.organizationIds, "organizationsOp", p.organizationsOp);
  putList(query, "plans", p.plans, "plansOp", p.plansOp);
  putList(query, "lifecycles", p.lifecycles, "lifecyclesOp", p.lifecyclesOp);
  putList(query, "accountLeads", p.accountLeads, "accountLeadsOp", p.accountLeadsOp);
  return query;
}

export function userListQuery(p: UserListParams): Record<string, string> {
  const query: Record<string, string> = {};
  if (p.search) query.search = p.search;
  putList(query, "roles", p.roles, "rolesOp", p.rolesOp);
  putList(query, "statuses", p.statuses, "statusesOp", p.statusesOp);
  putList(query, "plans", p.plans, "plansOp", p.plansOp);
  if (p.gfgOnly) query.gfgOnly = "true";
  if (p.demoOnly) query.demoOnly = "true";
  return query;
}

/** Account Lead panel options: "Unassigned" first (D5), then leads in their given order. */
export function accountLeadOptions(leads: AccountLead[]): FilterOption[] {
  return [
    { value: ACCOUNT_LEAD_UNASSIGNED, label: "Unassigned" },
    ...leads.map((lead) => ({ value: String(lead.id), label: lead.name })),
  ];
}
